import {memo, useState, useEffect, useRef} from 'react';
import styled from 'styled-components';
import {Palette} from 'hubbl-shared';
import {FontConfig} from '../../constants/FontConfig';

interface AnimatedProgressBarProps {
  percentage: number;
  duration?: number;
}

const ProgressBarContainer = styled.div`
  width: 100%;
  height: 24px;
  background-color: ${Palette.almostBlack750};
  border-radius: 12px;
  overflow: hidden;
  position: relative;
  margin-bottom: 12px;
`;

const ProgressBar = styled.div<{percentage: number; duration?: number}>`
  height: 100%;
  width: ${props => props.percentage}%;
  background: linear-gradient(
    90deg,
    ${Palette.primaryTerraCotta} 0%,
    ${Palette.primaryCherokee} 100%
  );
  border-radius: 12px;
  position: relative;
  transition: width ${props => props.duration || 1500}ms ease-out;
  will-change: width;
`;

const ProgressText = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: ${Palette.white};
  font-family: ${FontConfig.Heading6.fontFamily};
  font-size: ${FontConfig.Heading7.fontSize}px;
  font-weight: ${FontConfig.Heading6.fontWeight};
  letter-spacing: ${FontConfig.Heading6.letterSpacing}px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  white-space: nowrap;
  min-width: fit-content;
`;

// Custom hook for animated progress bar
const useAnimatedProgress = (
  targetPercentage: number,
  duration: number = 1500,
) => {
  const [currentPercentage, setCurrentPercentage] = useState(0);
  const animationRef = useRef<number>();
  const startTimeRef = useRef<number>();
  const lastUpdateRef = useRef<number>(0);

  useEffect(() => {
    // Reset if target changes
    setCurrentPercentage(0);
    lastUpdateRef.current = 0;

    const startTime = performance.now();
    startTimeRef.current = startTime;

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Easing function for smooth animation
      const easeOut = 1 - Math.pow(1 - progress, 3);
      const newPercentage = targetPercentage * easeOut;

      // Only update state if the change is significant enough (throttle updates)
      if (Math.abs(newPercentage - lastUpdateRef.current) >= 0.5) {
        setCurrentPercentage(newPercentage);
        lastUpdateRef.current = newPercentage;
      }

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        setCurrentPercentage(targetPercentage);
      }
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [targetPercentage, duration]);

  return currentPercentage;
};

export const AnimatedProgressBar = memo(
  ({percentage, duration = 1500}: AnimatedProgressBarProps) => {
    const animatedPercentage = useAnimatedProgress(percentage, duration);

    return (
      <ProgressBarContainer>
        <ProgressBar percentage={animatedPercentage} duration={duration}>
          <ProgressText>{Math.round(animatedPercentage)}%</ProgressText>
        </ProgressBar>
      </ProgressBarContainer>
    );
  },
);
